import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX, Play } from 'lucide-react';
import { useVideoPlayer } from '../context/VideoPlayerContext';
import { useInViewport } from '../hooks/useInViewport';
import { recordMediaEvent } from '../lib/mediaTelemetry';

/**
 * FeedVideo — Phase 28 + Phase 49 rework · Feb 2026.
 * -----------------------------------------------------------------
 * Autoplay-aware, low-memory feed video with strict mobile hygiene:
 *
 * • The <video> element is ONLY mounted after the container has
 *   entered the viewport once (with a 400px pre-warm rootMargin).
 *   Before that a lightweight <div> placeholder occupies the exact
 *   aspect-ratio slot so scrolling never triggers layout shift.
 * • `preload` cascades: NOT mounted → nothing; mounted & idle →
 *   "none"; entered viewport → "metadata". This bounds network cost
 *   even in long feeds.
 * • Global "active video" lock via <VideoPlayerContext> guarantees
 *   at most one video is unpaused at any moment. Everyone else pauses.
 * • Mute-by-default + `playsInline` — iOS Safari allows autoplay only
 *   under these two constraints. The Play/Tap hint appears only if
 *   the browser explicitly refuses the muted autoplay attempt.
 *
 * Props:
 *   • src       — media URL
 *   • postId    — unique id used as the global lock key
 *   • width     — original media width (px) for aspect-ratio
 *   • height    — original media height (px) for aspect-ratio
 *   • poster    — optional cover image
 */
const FeedVideo = ({ src, postId, width = 0, height = 0, poster }) => {
  const videoRef = useRef(null);
  const { activeId, setActive, isActive, muted, toggleMute } = useVideoPlayer();
  const [showTapHint, setShowTapHint] = useState(false);
  const playCountedRef = useRef(false);
  // Phase 49 · defer mounting until visible; then keep mounted so a
  // downward scroll that briefly leaves the viewport doesn't force a
  // costly re-download.
  const { ref: containerRef, inView, hasEnteredOnce } = useInViewport({
    rootMargin: '400px',
    threshold: [0, 0.2, 0.6, 1],
  });

  // Compute aspect ratio with safe fallback so both the placeholder
  // and the actual <video> reserve the same box → zero layout shift.
  const aspectStyle = (() => {
    if (width > 0 && height > 0) {
      const ratio = Math.max(0.4, Math.min(1.9, width / height));
      return { aspectRatio: String(ratio) };
    }
    return { aspectRatio: '1 / 1' };
  })();

  // Claim/release the global "active video" lock based on the
  // realtime intersection ratio. We use a secondary observer here
  // instead of `inView` because we need a stricter 0.6 threshold to
  // avoid two videos ping-ponging the lock during a fast scroll.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !hasEnteredOnce) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.intersectionRatio >= 0.6) {
            setActive(postId);
          } else if (e.intersectionRatio < 0.2 && isActive(postId)) {
            setActive(null);
          }
        }
      },
      { threshold: [0, 0.2, 0.6, 1] },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasEnteredOnce, postId]);

  // React to global activeId changes — play if I'm it, pause otherwise.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (activeId === postId) {
      v.muted = muted;
      const p = v.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          // Some browsers still refuse autoplay if unmuting happened
          // too early. Force-mute and retry once.
          v.muted = true;
          v.play().catch(() => {
            // Phase 51.2 · DEFINITIVE fix for the "Tocca per riprodurre"
            // ghost: on some devices the rejected play() promise races
            // with the browser starting playback anyway. Re-check the
            // actual element state after a short beat — only surface the
            // hint if the video is REALLY still paused. The `onPlaying`
            // handler below removes it in any case the instant playback
            // starts (identical behaviour on Android/iOS/desktop/PWA).
            setTimeout(() => {
              const el = videoRef.current;
              if (el && el.paused && isActive(postId)) {
                setShowTapHint(true);
                recordMediaEvent('video_tap_hint');
              }
            }, 350);
          });
        });
      }
    } else {
      try { v.pause(); } catch { /* noop */ }
    }
  }, [activeId, postId, muted]);

  // Sync the muted attribute live so unmute takes effect mid-playback.
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.muted = muted;
  }, [muted]);

  const handleTap = () => {
    setShowTapHint(false);
    if (!isActive(postId)) {
      setActive(postId);
      return;
    }
    toggleMute();
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden bg-black"
      style={aspectStyle}
      data-testid={`feed-video-${postId}`}
    >
      {/* Skeleton placeholder — reserves the aspect slot so the layout
          doesn't jump when the <video> mounts. Poster is displayed if
          available so users see a static preview even before the real
          media is fetched. */}
      {!hasEnteredOnce && (
        poster ? (
          <img
            src={poster}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
            data-testid={`feed-video-poster-${postId}`}
          />
        ) : (
          <div className="absolute inset-0 animate-pulse bg-neutral-900" data-testid={`feed-video-skeleton-${postId}`} />
        )
      )}

      {hasEnteredOnce && (
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          className="h-full w-full object-contain"
          playsInline
          loop
          muted={muted}
          // Phase 49 · adaptive preload: metadata only once we've been
          // inside the viewport at least once. Never fetch beyond
          // metadata unless we're the active player.
          preload={inView ? 'metadata' : 'none'}
          onClick={handleTap}
          // Phase 51.1 · BUGFIX — the "Tocca per riprodurre" hint used to
          // stay on screen even when a late/retried autoplay eventually
          // succeeded (race between the rejected play() promise and the
          // browser starting playback anyway). The `playing` event is the
          // source of truth: the moment real playback starts, kill the hint.
          onPlaying={() => {
            setShowTapHint(false);
            if (!playCountedRef.current) {
              playCountedRef.current = true;
              recordMediaEvent('video_autoplay');
            }
          }}
          onLoadedData={() => recordMediaEvent('video_loaded')}
        />
      )}

      {/* Mute / Unmute pill — bottom-right */}
      {hasEnteredOnce && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggleMute(); }}
          className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/75"
          aria-label={muted ? 'Attiva audio' : 'Disattiva audio'}
          data-testid={`feed-video-mute-${postId}`}
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      )}

      {showTapHint && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-sm text-white">
            <Play className="h-4 w-4" /> Tocca per riprodurre
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(FeedVideo);
